// components/EmailTemplates/TestEmailModal.js
import React, {useEffect, useState} from "react";
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Code,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Textarea,
    useColorModeValue,
    VStack,
} from "@chakra-ui/react";
import {FiEye, FiRefreshCw, FiSend} from "react-icons/fi";
import {getDefaultTestData, isValidJSON} from "./utils/EmailTemplateUtils";

const TestEmailModal = ({
                            isOpen,
                            onClose,
                            template = null,
                            onSendTest,
                            loading = false
                        }) => {
    const [testEmail, setTestEmail] = useState('');
    const [testData, setTestData] = useState('{}');
    const [jsonError, setJsonError] = useState('');
    const [previewContent, setPreviewContent] = useState({subject: '', html: '', text: ''});
    const [activeTab, setActiveTab] = useState(0);

    // Color mode values
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");

    // Initialize test data when template changes
    useEffect(() => {
        if (template && isOpen) {
            const defaultData = getDefaultTestData(template.type);
            setTestData(JSON.stringify(defaultData, null, 2));
            updatePreview(template, defaultData);
        }
    }, [template, isOpen]);

    // Validate JSON and update preview when test data changes
    useEffect(() => {
        if (template) {
            try {
                const parsedData = JSON.parse(testData);
                setJsonError('');
                updatePreview(template, parsedData);
            } catch (error) {
                setJsonError('Invalid JSON format');
                setPreviewContent({subject: '', html: '', text: ''});
            }
        }
    }, [testData, template]);

    const updatePreview = (templateData, variables) => {
        try {
            let subject = templateData.subject || '';
            let htmlContent = templateData.htmlContent || '';
            let textContent = templateData.textContent || '';

            // Replace variables in content
            Object.entries(variables).forEach(([key, value]) => {
                const placeholder = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
                const stringValue = String(value || '');

                subject = subject.replace(placeholder, stringValue);
                htmlContent = htmlContent.replace(placeholder, stringValue);
                textContent = textContent.replace(placeholder, stringValue);
            });

            setPreviewContent({
                subject,
                html: htmlContent,
                text: textContent,
            });
        } catch (error) {
            console.error('Error updating preview:', error);
        }
    };

    const handleSendTest = () => {
        if (!testEmail.trim()) {
            setJsonError('Please enter a test email address');
            return;
        }

        if (!isValidJSON(testData)) {
            setJsonError('Please provide valid JSON test data');
            return;
        }

        try {
            const parsedData = JSON.parse(testData);
            onSendTest({
                testEmail: testEmail.trim(),
                testData: parsedData,
            });
        } catch (error) {
            setJsonError('Invalid JSON format');
        }
    };

    const handleLoadDefaultData = () => {
        if (template) {
            const defaultData = getDefaultTestData(template.type);
            setTestData(JSON.stringify(defaultData, null, 2));
        }
    };

    const handleReset = () => {
        setTestEmail('');
        setTestData('{}');
        setJsonError('');
        setPreviewContent({subject: '', html: '', text: ''});
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent maxH="90vh">
                <ModalHeader>
                    <Flex align="center" gap={2}>
                        <FiSend/>
                        Send Test Email
                        {template && (
                            <Badge colorScheme="blue" ml={2}>
                                {template.name}
                            </Badge>
                        )}
                    </Flex>
                </ModalHeader>
                <ModalCloseButton/>

                <ModalBody>
                    <Tabs index={activeTab} onChange={setActiveTab}>
                        <TabList>
                            <Tab>Setup</Tab>
                            <Tab>Preview</Tab>
                            <Tab>Variables</Tab>
                        </TabList>

                        <TabPanels>
                            {/* Setup Tab */}
                            <TabPanel>
                                <VStack spacing={4} align="stretch">
                                    <FormControl isRequired>
                                        <FormLabel>Test Email Address</FormLabel>
                                        <Input
                                            type="email"
                                            value={testEmail}
                                            onChange={(e) => setTestEmail(e.target.value)}
                                            placeholder="test@example.com"
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <FormLabel mb={0}>Test Data (JSON)</FormLabel>
                                            <HStack spacing={2}>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    leftIcon={<FiRefreshCw/>}
                                                    onClick={handleLoadDefaultData}
                                                >
                                                    Load Default
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={handleReset}
                                                >
                                                    Reset
                                                </Button>
                                            </HStack>
                                        </Flex>
                                        <Textarea
                                            value={testData}
                                            onChange={(e) => setTestData(e.target.value)}
                                            placeholder='{"user_name": "John Doe", "order_id": "12345"}'
                                            rows={12}
                                            fontFamily="monospace"
                                            fontSize="sm"
                                        />
                                        {jsonError && (
                                            <Alert status="error" mt={2}>
                                                <AlertIcon/>
                                                <AlertDescription>{jsonError}</AlertDescription>
                                            </Alert>
                                        )}
                                        <Text fontSize="sm" color="gray.500" mt={2}>
                                            Provide test data for template variables in JSON format.
                                            Click "Load Default" to get sample data for this template type.
                                        </Text>
                                    </FormControl>
                                </VStack>
                            </TabPanel>

                            {/* Preview Tab */}
                            <TabPanel>
                                <VStack spacing={4} align="stretch">
                                    {previewContent.subject && (
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Email Subject:</Text>
                                            <Box
                                                p={3}
                                                bg={cardBg}
                                                border="1px"
                                                borderColor={borderColor}
                                                borderRadius="md"
                                            >
                                                <Text>{previewContent.subject}</Text>
                                            </Box>
                                        </Box>
                                    )}

                                    <Divider/>

                                    <Tabs variant="enclosed" size="sm">
                                        <TabList>
                                            <Tab>HTML Preview</Tab>
                                            <Tab>HTML Source</Tab>
                                            <Tab>Plain Text</Tab>
                                        </TabList>

                                        <TabPanels>
                                            <TabPanel>
                                                <Box
                                                    border="1px"
                                                    borderColor={borderColor}
                                                    borderRadius="md"
                                                    p={4}
                                                    bg="white"
                                                    color="black"
                                                    maxH="400px"
                                                    overflowY="auto"
                                                >
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: previewContent.html
                                                        }}
                                                    />
                                                </Box>
                                            </TabPanel>

                                            <TabPanel>
                                                <Box
                                                    border="1px"
                                                    borderColor={borderColor}
                                                    borderRadius="md"
                                                    p={4}
                                                    bg={cardBg}
                                                    maxH="400px"
                                                    overflowY="auto"
                                                >
                                                    <Code
                                                        fontSize="sm"
                                                        whiteSpace="pre-wrap"
                                                        wordBreak="break-word"
                                                    >
                                                        {previewContent.html}
                                                    </Code>
                                                </Box>
                                            </TabPanel>

                                            <TabPanel>
                                                <Box
                                                    border="1px"
                                                    borderColor={borderColor}
                                                    borderRadius="md"
                                                    p={4}
                                                    bg={cardBg}
                                                    maxH="400px"
                                                    overflowY="auto"
                                                >
                                                    <Text
                                                        fontSize="sm"
                                                        whiteSpace="pre-wrap"
                                                        fontFamily="monospace"
                                                    >
                                                        {previewContent.text || 'No plain text content provided'}
                                                    </Text>
                                                </Box>
                                            </TabPanel>
                                        </TabPanels>
                                    </Tabs>
                                </VStack>
                            </TabPanel>

                            {/* Variables Tab */}
                            <TabPanel>
                                <VStack spacing={4} align="stretch">
                                    <Alert status="info">
                                        <AlertIcon/>
                                        <Box>
                                            <AlertTitle>Template Variables</AlertTitle>
                                            <AlertDescription>
                                                These are the variables available for this template type.
                                                Make sure your test data includes values for the variables you want to
                                                test.
                                            </AlertDescription>
                                        </Box>
                                    </Alert>

                                    {template?.variables && template.variables.length > 0 ? (
                                        <Box>
                                            <Text fontWeight="bold" mb={3}>Available Variables:</Text>
                                            <Flex wrap="wrap" gap={2}>
                                                {template.variables.map((variable, index) => (
                                                    <Badge
                                                        key={index}
                                                        variant="outline"
                                                        colorScheme="blue"
                                                        p={2}
                                                        fontSize="sm"
                                                    >
                                                        {variable}
                                                    </Badge>
                                                ))}
                                            </Flex>
                                        </Box>
                                    ) : (
                                        <Text color="gray.500">No variables defined for this template.</Text>
                                    )}

                                    <Divider/>

                                    <Box>
                                        <Text fontWeight="bold" mb={3}>Current Test Data Variables:</Text>
                                        {isValidJSON(testData) ? (
                                            <Box>
                                                {Object.entries(JSON.parse(testData)).map(([key, value]) => (
                                                    <Flex key={key} justify="space-between" py={1}>
                                                        <Code fontSize="sm">{'{{' + key + '}}'}</Code>
                                                        <Text fontSize="sm" color="gray.600">
                                                            {String(value).length > 50
                                                                ? String(value).substring(0, 50) + '...'
                                                                : String(value)}
                                                        </Text>
                                                    </Flex>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Text color="red.500">Invalid JSON - cannot display variables</Text>
                                        )}
                                    </Box>
                                </VStack>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        leftIcon={<FiEye/>}
                        variant="outline"
                        mr={3}
                        onClick={() => setActiveTab(1)}
                    >
                        Preview
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={handleSendTest}
                        isLoading={loading}
                        loadingText="Sending..."
                        leftIcon={<FiSend/>}
                        isDisabled={!testEmail.trim() || !isValidJSON(testData) || !!jsonError}
                    >
                        Send Test Email
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default TestEmailModal;
